import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router";
import { API_URLS } from "src/api/api-urls";
import useApiServices from "src/api/use-api-services";
import { PlatformsEnum } from "src/constants/platforms";
import { useBoolean } from "src/hooks/use-boolean";
import useTable from "src/hooks/use-table/use-table";
import { paths } from "src/routes/paths";
import { IActivitiesListDto, IActivityItemDto } from "src/types/activities";
import axiosInstance from "src/utils/axios";

const useReportsSection = () => {
  // #region States and hooks
  const pagination = useTable();
  const addDialog = useBoolean();
  const queryClient = useQueryClient();
  const isDropdownOpen = useBoolean(true);
  const searchParams = new URLSearchParams(window.location.search);
  const router = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { usePostDynamicUrlApi } = useApiServices();

  const selected = {
    keyword: searchParams.get("keyword"),
    startDate: searchParams.get("start_date"),
    endDate: searchParams.get("end_date"),
    platform: searchParams.get("platform"),
  };

  const isSelected = (activity: IActivityItemDto) => {
    return (
      activity.keyword === selected.keyword &&
      activity.start_date === selected.startDate &&
      activity.end_date === selected.endDate &&
      activity.platform === selected.platform
    );
  };
  // #endregion

  // #region Services
  const fetchActivities = async ({ pageParam = 1 }): Promise<IActivitiesListDto> => {
    return axiosInstance.get(API_URLS.ACTIVITY, {
      params: {
        page: pageParam,
        per_page: 10,
      },
    });
  };

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery<IActivitiesListDto>({
      queryKey: [API_URLS.ACTIVITY + "list" + "pages"],
      queryFn: fetchActivities,
      getNextPageParam: (lastPage) => {
        return lastPage.metadata.has_more ? lastPage.metadata.current_page + 1 : undefined;
      },
    });

  const isTheirNoData =
    data?.pages?.[0]?.metadata.total_pages !== undefined &&
    +data?.pages?.[0]?.metadata.total_pages === 0;
  // #endregion

  // #region Handlers
  const handleSelectActivity = (activity: IActivityItemDto) => {
    if (activity.status === "cancelled") {
      return;
    }

    router(
      paths.custom[activity.platform as PlatformsEnum]({
        start_date: activity.start_date,
        end_date: activity.end_date,
        keyword: activity.keyword,
        platform: activity.platform,
      } as unknown as Record<string, string>),
      {
        // Note that we are passing activity object as state in order to check its status
        // in analysis page, as you can check dashboard-view-layout.tsx
        state: {
          activity,
        },
      }
    );
  };

  const {
    mutate: cancelActivity,
    isLoading: isCancelling,
    variables: cancellingActivityId,
  } = usePostDynamicUrlApi<unknown, number>({
    buildUrl: (activityId) => API_URLS.custom.SCRAP_CANCELLATION(activityId),
    requestBody: undefined,
    urlAfterSuccess: API_URLS.ACTIVITY + "list" + "pages",
    onSuccess: () => {
      enqueueSnackbar("Scraping cancelled", { variant: "info" });
      queryClient.invalidateQueries({ queryKey: [API_URLS.ACTIVITY + "list" + "pages"] });
    },
    onError: () => {
      enqueueSnackbar("Unable to cancel report", { variant: "error" });
    },
  });

  const handleCancelActivity = (activityId: number) => {
    cancelActivity(activityId);
  };

  const isActivityCancelable = (activity: IActivityItemDto) =>
    activity.status === "pending" || activity.status === "running";

  const isCancellingActivity = (activityId: number) =>
    isCancelling && cancellingActivityId === activityId;

  // #endregion

  const activityStatus = (
    activity: IActivityItemDto
  ): {
    icon: string;
    color: string;
  } => {
    switch (activity.status) {
      case "pending":
      case "running":
        return { icon: "🟡", color: "warning.main" };
      case "completed":
        return { icon: "🟢", color: "success.main" };
      case "failed":
        return { icon: "🔴", color: "error.main" };
      case "cancelled":
        return { icon: "🔴", color: "text.disabled" };
      default:
        return { icon: "🟢", color: "success.main" };
    }
  };

  return {
    addDialog,
    isDropdownOpen,
    pagination,
    data,
    isLoading,
    isTheirNoData,
    handleSelectActivity,
    activityStatus,
    isSelected,
    selected,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    handleCancelActivity,
    isActivityCancelable,
    isCancellingActivity,
  };
};

export default useReportsSection;
